﻿import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Wallet } from 'lucide-react';
import { createAdvanceSchema, type CreateAdvanceInput, type ApiError } from '@pms/shared';
import { payrollService } from '@/services/payroll.service';
import { usersService } from '@/services/users.service';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogOverlay } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AddAdvanceModal({ open, onClose }: Props) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const { data: laConfig } = useQuery({
    queryKey: ['payroll', 'la-config'],
    queryFn: () => payrollService.getLAConfig(),
  });

  const maxAdvanceAmount = laConfig ? parseFloat(laConfig.max_advance_amount) : 100000;
  const reasonRequired   = laConfig?.require_advance_reason ?? false;

  const { data: staffData, isLoading: staffLoading, isError: staffError } = useQuery({
    queryKey: ['staff', { limit: 100 }],
    queryFn: () => usersService.list({ limit: 100, is_active: true }),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateAdvanceInput>({
    resolver: zodResolver(createAdvanceSchema),
    defaultValues: {
      advance_date:    today,
      deduction_year:  currentYear,
      deduction_month: currentMonth,
    },
  });

  const advanceDate = watch('advance_date');
  const deductionValue = `${watch('deduction_year')}-${watch('deduction_month')}`;

  // Last month through six months ahead — the salary the advance is recovered from.
  const deductionOptions = Array.from({ length: 8 }, (_, i) => {
    const d = new Date(currentYear, currentMonth - 2 + i);
    return {
      value: `${d.getFullYear()}-${d.getMonth() + 1}`,
      label: d.toLocaleString('en-IN', { month: 'long', year: 'numeric' }),
    };
  });

  const create = useMutation({
    mutationFn: (data: CreateAdvanceInput) => payrollService.createAdvance(data),
    onSuccess: () => {
      toast.success('Advance created successfully');
      void queryClient.invalidateQueries({ queryKey: ['payroll', 'advances'] });
      reset();
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogOverlay />
      <DialogContent className="max-w-md">
        <DialogHeader className="mb-4">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Wallet className="h-4 w-4 text-teal-600" />
            Add Advance
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="adv_user_id">Employee *</Label>
            <Select
              value={watch('user_id') ?? ''}
              onValueChange={(v) => setValue('user_id', v, { shouldValidate: true })}
              disabled={staffLoading}
            >
              <SelectTrigger id="adv_user_id">
                <SelectValue placeholder={staffLoading ? 'Loading employees…' : 'Select employee'} />
              </SelectTrigger>
              <SelectContent>
                {staffData?.data.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.first_name} {s.last_name} {s.employee_code ? `(${s.employee_code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {staffError && (
              <p className="text-xs text-amber-600">Failed to load employees — check permissions or network.</p>
            )}
            {!staffLoading && !staffError && (staffData?.data.length ?? 0) === 0 && (
              <p className="text-xs text-muted-foreground">No active employees found. Create staff members first.</p>
            )}
            {errors.user_id && <p className="text-xs text-red-500">{errors.user_id.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="adv_amount">
                Amount (₹) * (Max: ₹{maxAdvanceAmount.toLocaleString('en-IN')})
              </Label>
              <Input
                id="adv_amount"
                type="number"
                min="1"
                max={maxAdvanceAmount}
                {...register('amount')}
              />
              {errors.amount && <p className="text-xs text-red-500">{errors.amount.message}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="adv_date">Advance Date</Label>
              <DatePicker id="adv_date" value={advanceDate ?? ''} onChange={(v) => setValue('advance_date', v)} modal={false} />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="adv_deduct">Deduct from salary of</Label>
            <Select
              value={deductionValue}
              onValueChange={(v) => {
                const [y, m] = v.split('-').map(Number) as [number, number];
                setValue('deduction_year', y);
                setValue('deduction_month', m);
              }}
            >
              <SelectTrigger id="adv_deduct"><SelectValue /></SelectTrigger>
              <SelectContent>
                {deductionOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">The full amount is deducted from that month's salary automatically.</p>
          </div>

          <div className="space-y-1">
            <Label htmlFor="adv_reason">
              Reason {reasonRequired ? '*' : ''}
            </Label>
            <Textarea
              id="adv_reason"
              placeholder="Reason for advance…"
              rows={2}
              required={reasonRequired}
              {...register('reason')}
            />
            {reasonRequired && (
              <p className="text-xs text-muted-foreground">Required by your organisation's policy.</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add Advance'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
